import React from 'react';
import {
  FileText,
  Search,
  Download as ArrowDownTrayIcon,
  Upload,
  ChevronDown,
  LayoutGrid,
  Printer,
  Trash2,
  FileOutput,
  FileSpreadsheet,
} from 'lucide-react';
import { useGlobalTheme } from '../../shared/hooks/useGlobalTheme';
import { useTranslation } from 'react-i18next';
import FacturationViewModeToggle from './components/FacturationViewModeToggle';
import { isSaudiBrand } from '../../products/saudi/utils/isSaudiBrand';
import { QAWAEM_TABLE_TOOLBAR_FILTER_BTN } from '../../products/saudi/utils/qawaemDropdownStyles';

/** Panneaux Import / Export — même recette FacturiersPage (gradient slate → blue-750 → slate, halo bleu). */
const ARCHIVES_IE_DROPDOWN_PANEL_CLASS =
  'border border-blue-400/60 rounded-2xl shadow-2xl min-w-[160px]';

const ARCHIVES_IE_DROPDOWN_PANEL_STYLE = {
  position: 'absolute',
  top: 'calc(100% + 8px)',
  right: 0,
  zIndex: 40,
  backgroundImage:
    'linear-gradient(to bottom right, rgb(51, 65, 85), rgb(29, 71, 196), rgb(51, 65, 85))',
  boxShadow:
    '0 8px 32px rgba(59, 130, 246, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.2)',
  overflow: 'visible',
};

const ARCHIVES_IE_MENU_ITEM_CLASS =
  'w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl';

/**
 * Shared Archives toolbar: search, grille/table toggle, import, export, bulk print.
 * Used by Vue Générale and aggregate tabs (Par client / Par type / Par opération).
 */
export default function ArchivesToolbarFiltersRow({
  t,
  searchPlaceholder,
  searchTerm,
  setSearchTerm,
  viewMode,
  setViewMode,
  isImportOpen,
  setIsImportOpen,
  importRef,
  isExportOpen,
  setIsExportOpen,
  exportRef,
  handleImportCSV,
  handleImportExcel,
  handleImportJSON,
  handleImportXML,
  handleImportTXT,
  handleExportPDF,
  handleExportExcel,
  handleExportCSV,
  handleExportJSON,
  handleExportXML,
  handleExportHTML,
  handleExportTXT,
  isGeneratingPDF,
  isGeneratingExcel,
  selectedBulkCount = 0,
  onBulkPrintArchives,
  onBulkDeleteArchives,
  variant = 'strip',
  hideViewToggle = false,
}) {
  const { t: tHook } = useTranslation();
  const p = (key, opts) => tHook(`modules.facturation.pages.archivesToolbarFiltersRow.${key}`, opts);
  const { isDashboardLightMode } = useGlobalTheme();
  const placeholder = searchPlaceholder ?? p('search_document');

  const ieMenuItemClass = isDashboardLightMode
    ? 'w-full px-4 py-2.5 text-left text-slate-800 hover:bg-slate-100 transition-colors flex items-center gap-3 rounded-xl'
    : ARCHIVES_IE_MENU_ITEM_CLASS;

  const ieDropdownPanelClass = `${ARCHIVES_IE_DROPDOWN_PANEL_CLASS}${isDashboardLightMode ? ' !bg-white border-gray-200' : ''}`;

  const ieDropdownPanelStyle = isDashboardLightMode
    ? {
        ...ARCHIVES_IE_DROPDOWN_PANEL_STYLE,
        backgroundImage: 'none',
        backgroundColor: '#ffffff',
        boxShadow: '0 10px 40px rgba(15, 23, 42, 0.12)',
        border: '1px solid rgb(226 232 240)',
      }
    : ARCHIVES_IE_DROPDOWN_PANEL_STYLE;

  const isTitleRow = variant === 'titleRow';
  const qawaem = isSaudiBrand();

  const ieTriggerClass =
    isTitleRow && qawaem
      ? QAWAEM_TABLE_TOOLBAR_FILTER_BTN
      : null;

  const importBtnClass =
    ieTriggerClass ||
    'bg-orange-500/20 hover:bg-orange-500/30 text-orange-200 border border-orange-500/30 px-3 py-2 rounded-lg transition-all duration-200 flex items-center gap-1.5 shadow-sm';

  const exportBtnClass =
    ieTriggerClass ||
    'toolbar-export-trigger bg-green-500/20 hover:bg-green-500/30 text-green-200 border border-green-500/30 px-3 py-2 rounded-lg transition-all duration-200 flex items-center gap-1.5 shadow-sm';

  const printBtnClass =
    ieTriggerClass ||
    'bg-slate-500/20 hover:bg-slate-500/30 text-slate-200 border border-slate-500/30 disabled:cursor-not-allowed disabled:opacity-45 px-3 py-2 rounded-lg transition-all duration-200 flex items-center gap-1.5 shrink-0 shadow-sm';

  return (
    <div
      className={`facturation-list-controls-row archives-toolbar-filters flex min-w-0 flex-row flex-wrap items-center gap-2 lg:gap-3 ${
        isTitleRow ? 'archives-toolbar-filters--title-row' : ''
      } ${isDashboardLightMode ? 'archives-toolbar--light' : ''}`}
    >
      {viewMode === 'cards' ? (
        <div className="relative min-w-0 flex-1 max-w-md archives-toolbar-search">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/70 pointer-events-none" />
          <input
            type="text"
            placeholder={placeholder}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-white/10 border border-white/20 rounded-xl text-white text-sm placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
          />
        </div>
      ) : null}

      <div
        className={`facturation-list-toolbar flex shrink-0 flex-wrap items-center gap-2 ${
          isTitleRow ? '' : 'ml-auto'
        }`}
      >
        {!hideViewToggle && (
          <FacturationViewModeToggle
            viewMode={viewMode}
            onViewModeChange={setViewMode}
            isLight={isDashboardLightMode}
            CardsIcon={LayoutGrid}
            cardsTitle={p('view_grid')}
            tableTitle={p('view_list')}
          />
        )}

        {selectedBulkCount > 0 && typeof onBulkDeleteArchives === 'function' && (
          <button
            type="button"
            onClick={() => {
              setIsImportOpen(false);
              setIsExportOpen(false);
              onBulkDeleteArchives();
            }}
            className="flex shrink-0 items-center gap-2 px-3 py-2 bg-red-500/20 hover:bg-red-500/30 border border-red-500/30 text-red-200 rounded-lg transition-all text-sm font-medium"
          >
            <Trash2 size={16} aria-hidden />
            <span className="hidden sm:inline">{p('delete_with_count', { count: selectedBulkCount })}</span>
          </button>
        )}

        <div className="relative z-30" ref={importRef}>
          <button
            type="button"
            onClick={() => {
              setIsImportOpen(!isImportOpen);
              setIsExportOpen(false);
            }}
            className={importBtnClass}
          >
            <Upload size={16} />
            <span className="text-sm font-medium">{p('import')}</span>
            <ChevronDown size={14} className={`transition-transform ${isImportOpen ? 'rotate-180' : ''}`} />
          </button>

            {isImportOpen && (
            <div className={ieDropdownPanelClass} style={ieDropdownPanelStyle}>
              <div className="p-2">
              <button
                type="button"
                onClick={() => {
                  handleImportCSV();
                  setIsImportOpen(false);
                }}
                className={ieMenuItemClass}
              >
                <FileOutput size={16} className="text-purple-400" />
                <span className="text-sm font-medium">{p('csv')}</span>
              </button>
              <button
                type="button"
                onClick={() => {
                  handleImportExcel();
                  setIsImportOpen(false);
                }}
                className={ieMenuItemClass}
              >
                <FileSpreadsheet size={16} className="text-green-400" />
                <span className="text-sm font-medium">{p('excel')}</span>
              </button>
              <button
                type="button"
                onClick={() => {
                  handleImportJSON();
                  setIsImportOpen(false);
                }}
                className={ieMenuItemClass}
              >
                <FileText size={16} className="text-yellow-400" />
                <span className="text-sm font-medium">{p('json')}</span>
              </button>
              <button
                type="button"
                onClick={() => {
                  handleImportXML();
                  setIsImportOpen(false);
                }}
                className={ieMenuItemClass}
              >
                <FileText size={16} className="text-orange-400" />
                <span className="text-sm font-medium">{p('xml')}</span>
              </button>
              <button
                type="button"
                onClick={() => {
                  handleImportTXT();
                  setIsImportOpen(false);
                }}
                className={ieMenuItemClass}
              >
                <FileText size={16} className="text-gray-400" />
                <span className="text-sm font-medium">{p('txt_tsv')}</span>
              </button>
              </div>
            </div>
          )}
        </div>

        <div className="relative z-30" ref={exportRef}>
          <button
            type="button"
            onClick={() => {
              setIsExportOpen(!isExportOpen);
              setIsImportOpen(false);
            }}
            className={exportBtnClass}
          >
            <ArrowDownTrayIcon size={16} />
            <span className="text-sm font-medium">{p('export')}</span>
            <ChevronDown size={14} className={`transition-transform ${isExportOpen ? 'rotate-180' : ''}`} />
          </button>

            {isExportOpen && (
            <div className={`${ieDropdownPanelClass} toolbar-export-panel`} style={ieDropdownPanelStyle}>
              <div className="p-2">
              <button
                type="button"
                data-export-format="PDF"
                onClick={() => {
                  handleExportPDF();
                  setIsExportOpen(false);
                }}
                disabled={isGeneratingPDF}
                className={`${ieMenuItemClass} disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                <FileText size={16} className="text-red-400" />
                <span className="text-sm font-medium">
                  {isGeneratingPDF ? p('generating') : p('pdf')}
                </span>
              </button>
              <button
                type="button"
                data-export-format="Excel"
                onClick={() => {
                  handleExportExcel();
                  setIsExportOpen(false);
                }}
                disabled={isGeneratingExcel}
                className={`${ieMenuItemClass} disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                <FileSpreadsheet size={16} className="text-green-400" />
                <span className="text-sm font-medium">
                  {isGeneratingExcel ? p('generating') : p('excel')}
                </span>
              </button>
              <button
                type="button"
                data-export-format="CSV"
                onClick={() => {
                  handleExportCSV();
                  setIsExportOpen(false);
                }}
                className={ieMenuItemClass}
              >
                <FileOutput size={16} className="text-purple-400" />
                <span className="text-sm font-medium">{p('csv')}</span>
              </button>
              <button
                type="button"
                onClick={() => {
                  handleExportJSON();
                  setIsExportOpen(false);
                }}
                className={ieMenuItemClass}
              >
                <FileText size={16} className="text-yellow-400" />
                <span className="text-sm font-medium">{p('json')}</span>
              </button>
              <button
                type="button"
                onClick={() => {
                  handleExportXML();
                  setIsExportOpen(false);
                }}
                className={ieMenuItemClass}
              >
                <FileText size={16} className="text-orange-400" />
                <span className="text-sm font-medium">{p('xml')}</span>
              </button>
              <button
                type="button"
                onClick={() => {
                  handleExportHTML();
                  setIsExportOpen(false);
                }}
                className={ieMenuItemClass}
              >
                <FileText size={16} className="text-blue-300" />
                <span className="text-sm font-medium">{p('html')}</span>
              </button>
              <button
                type="button"
                onClick={() => {
                  handleExportTXT();
                  setIsExportOpen(false);
                }}
                className={ieMenuItemClass}
              >
                <FileText size={16} className="text-gray-400" />
                <span className="text-sm font-medium">{p('txt')}</span>
              </button>
              </div>
            </div>
          )}
        </div>

        {typeof onBulkPrintArchives === 'function' && (
          <button
            type="button"
            onClick={() => {
              setIsImportOpen(false);
              setIsExportOpen(false);
              onBulkPrintArchives();
            }}
            disabled={isGeneratingPDF || !selectedBulkCount}
            title={!selectedBulkCount ? p('bulk_print_select_one') : p('bulk_print_title')}
            className={printBtnClass}
          >
            <Printer size={16} />
            <span className="text-sm font-medium whitespace-nowrap">
              {selectedBulkCount > 0 ? p('bulk_print_with_count', { count: selectedBulkCount }) : p('bulk_print_more')}
            </span>
          </button>
        )}
      </div>
    </div>
  );
}


